import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GlobalStateContext } from '../utils/GlobalStateContext';
import { getUserId } from '../utils/utils';
import AdminRadTechAvailability from './AdminRadTechAvailability';
import AdminLabMachineAvailability from './AdminLabMachineAvailability';
import AdminMachineClosures from './AdminMachineClosures';
import ClinicClosures from './ClinicClosures';
import '../style/AdminDashboard.css';

// These two sections used to be tabs on the Admin Dashboard, which meant a rad
// tech or lab tech had to be given the `admin` role just to mark themselves out
// for a day. Roles here are physical tables (see ROLE_TABLES in
// middleware/auth.js), so a tech carrying the admin role instead of rad_tech
// disappears from `SELECT ... FROM rad_tech` — which is what
// /scheduling/radTechs reads, and what isModalityAvailableOnDate() uses to
// decide whether a modality can be booked at all. Giving techs their own way in
// removes the reason to over-grant admin in the first place.
//
// Reads were already open to every staff role (server.js mounts /scheduling
// behind requireRole(ALL_STAFF)); the writes were relaxed alongside this move,
// scoped so a tech can only edit their own calendar.
export default function StaffAvailability() {
    const BASE_URL = process.env.REACT_APP_API_URL;
    const { currentUser } = useContext(GlobalStateContext);
    const user = getUserId(currentUser);
    const navigate = useNavigate();

    // Lab staff open on the lab tab, everyone else on radiology.
    const LAB_ROLES = ['lab_tech', 'medical_assistant', 'office_assistant'];
    const [activeTab, setActiveTab] = useState(
        LAB_ROLES.includes(currentUser?.type) ? 'labMachines' : 'radTechs'
    );

    return (
        <div className="admin-dashboard">
            <ToastContainer position="top-right" autoClose={3000} theme="light" />

            <div className="admin-header">
                <button className="admin-back-btn" onClick={() => navigate('/home')}>← Back</button>
                <h1>Staff Availability</h1>
            </div>

            <div className="admin-tabs">
                <button
                    className={`admin-tab ${activeTab === 'radTechs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('radTechs')}
                >
                    Rad Tech Availability
                </button>
                <button
                    className={`admin-tab ${activeTab === 'labMachines' ? 'active' : ''}`}
                    onClick={() => setActiveTab('labMachines')}
                >
                    Lab Machine Availability
                </button>
                <button
                    className={`admin-tab ${activeTab === 'machineClosures' ? 'active' : ''}`}
                    onClick={() => setActiveTab('machineClosures')}
                >
                    Machine Closures
                </button>
                <button
                    className={`admin-tab ${activeTab === 'clinicClosures' ? 'active' : ''}`}
                    onClick={() => setActiveTab('clinicClosures')}
                >
                    Clinic Closures
                </button>
            </div>

            {activeTab === 'radTechs' && (
                <AdminRadTechAvailability
                    BASE_URL={BASE_URL}
                    currentUser={currentUser}
                    user={user}
                />
            )}

            {activeTab === 'labMachines' && (
                <AdminLabMachineAvailability
                    BASE_URL={BASE_URL}
                    currentUser={currentUser}
                    user={user}
                />
            )}

            {activeTab === 'machineClosures' && (
                <AdminMachineClosures
                    BASE_URL={BASE_URL}
                    currentUser={currentUser}
                    user={user}
                />
            )}

            {activeTab === 'clinicClosures' && (
                <ClinicClosures BASE_URL={BASE_URL} />
            )}
        </div>
    );
}
